// src/views/PartnerOffers/PartnerOffers.js
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { ALL_CURRENCIES } from "../../constants/currencies";
import {
  fetchCarrierPackages,
  fetchCarrierAdmin,
  fetchCarrierById,
  updateCarrierAdmin,
  updateCarrier,
  batchUpdateSelected,
  upsertRowPrice,
  downloadCarrierPackagesCsv,
  importCarrierPackagesCsv,
  setSuggestedPrice,
} from "../../services/CarrierService";
import MainInput from "../../components/Elements/MainInput/MainInput";
import MainButton from "../../components/Elements/MainButton/MainButton";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import CsvImportInfoModal from "../../components/Offers/CsvImportInfoModal/CsvImportInfoModal";
import sortUp from "../../assets/icons/data/sort_down.png";
import sortDown from "../../assets/icons/data/sort_down.png";
import "./PartnerOffers.css";

export default function PartnerOffers() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const { isAuthenticated, role, token, currency } = useContext(AuthContext);

  const [carrierInfo, setCarrierInfo] = useState(null);
  const [carrierLoading, setCarrierLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const [markupInput, setMarkupInput] = useState("");
  const [showInputValue, setShowInputValue] = useState(true);
  const [mrgInput, setMrgInput] = useState("");
  const [showMrgValue, setShowMrgValue] = useState(true);
  const [editingMarkup, setEditingMarkup] = useState(false);
  const [tempMarkup, setTempMarkup] = useState("");

  // Table state (only when hasCarrier)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("price");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showMine, setShowMine] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvInfoOpen, setCsvInfoOpen] = useState(false);

  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredRecords, setFilteredRecords] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [suggestedSaving, setSuggestedSaving] = useState({});

  const [editing, setEditing] = useState(null);
  const [draftRate, setDraftRate] = useState("");
  const [draftMrg, setDraftMrg] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingMrg, setEditingMrg] = useState(false);
  const [tempMrg, setTempMrg] = useState("");

  // map: rowId -> string (wartość wpisana)
  const [priceDrafts, setPriceDrafts] = useState({});
  // map: rowId -> boolean (czy zmieniono i pokazać check)
  const [priceDirty, setPriceDirty] = useState({});
  // map: rowId -> boolean (czy zapis w toku)
  const [priceSaving, setPriceSaving] = useState({});

  const optionalColumns = [
    { key: "id", label: "ID" },
    { key: "description", label: "Description" },
    { key: "automatic_price", label: "Auto Price" },
    { key: "top_up_available", label: "Top-up" },
  ];

  const alwaysVisibleColumns = [
    { key: "name", label: "Name" },
    { key: "gigabytes", label: "GB" },
    { key: "duration", label: "Duration" },
    { key: "country_id", label: "Country" },
    { key: "region_id", label: "Region" },
    { key: "active", label: "Active" },
    { key: "markup_rate", label: "Markup %" },
    { key: "markup_mrg", label: "MRG" },
    { key: "price", label: "Cost" },
    { key: "suggested_price", label: "Suggested" },
    { key: "carrier_price", label: "Your Price" },
    { key: "currency", label: "Currency" },
  ];

  const [visibleOptionals, setVisibleOptionals] = useState(() => {
    const raw = localStorage.getItem("partnerOffers.visibleOptionals");
    try {
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!token || role?.name !== "admin") {
      navigate(`/${currentLang}/sign-in`, { replace: true });
      return;
    }
  }, [role, token, navigate, currentLang]);

  useEffect(() => {
    localStorage.setItem("partnerOffers.visibleOptionals", JSON.stringify(visibleOptionals));
  }, [visibleOptionals]);

  const toggleColumn = (key) => {
    setVisibleOptionals((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  useEffect(() => {
    if (carrierInfo) {
      setDraftRate(String(carrierInfo.markup_rate ?? ""));
      setDraftMrg(String(carrierInfo.markup_mrg ?? ""));
    }
  }, [carrierInfo]);

  useEffect(() => {
    if (carrierInfo?.markup_mrg !== undefined && !editingMrg) {
      setMrgInput(carrierInfo.markup_mrg?.toString() ?? "");
      setShowMrgValue(true);
    }
  }, [carrierInfo, editingMrg]);

  useEffect(() => {
    if (!carrierInfo) return;
    setTempMarkup((carrierInfo.markup_rate ?? 0) * 100);
    setTempMrg(String(carrierInfo.markup_mrg ?? ""));
  }, [carrierInfo]);

  useEffect(() => {
    if (carrierInfo?.markup_rate !== undefined && !editingMarkup) {
      const pct = (carrierInfo.markup_rate * 100).toFixed(2);
      setTempMarkup(pct);
      setEditingMarkup(false);
    }
  }, [carrierInfo, editingMarkup]);

  const visibleColumns = useMemo(
    () => [...optionalColumns.filter((c) => visibleOptionals.includes(c.key)), ...alwaysVisibleColumns],
    [visibleOptionals],
  );

  const [filters, setFilters] = useState(() => {
    const raw = localStorage.getItem("partnerOffers.filters");
    try {
      return raw
        ? JSON.parse(raw)
        : {
            id: "",
            name: "",
            description: "",
            currency: "",
            min_price: "",
            max_price: "",
            min_gigabytes: "",
            max_gigabytes: "",
            min_duration: "",
            max_duration: "",
            top_up_available: "",
            country_id: "",
            region_id: "",
          };
    } catch {
      return {
        id: "",
        name: "",
        description: "",
        currency: "",
        min_price: "",
        max_price: "",
        min_gigabytes: "",
        max_gigabytes: "",
        min_duration: "",
        max_duration: "",
        top_up_available: "",
        country_id: "",
        region_id: "",
      };
    }
  });

  useEffect(() => {
    localStorage.setItem("partnerOffers.filters", JSON.stringify(filters));
  }, [filters]);

  const params = useMemo(() => {
    const p = {
      skip: (page - 1) * pageSize,
      limit: pageSize,
    };
    if (sortBy && sortOrder) {
      p.sort_by = sortBy;
      p.sort_order = sortOrder;
    }
    if (currency) p.currency = currency;
    if (filters.id) p.id = Number(filters.id);
    if (filters.name) p.name = filters.name;
    if (filters.description) p.description = filters.description;
    if (filters.currency) p.currency = filters.currency;
    if (filters.min_price) p.min_price = Number(filters.min_price);
    if (filters.max_price) p.max_price = Number(filters.max_price);
    if (filters.min_gigabytes) p.min_gigabytes = Number(filters.min_gigabytes);
    if (filters.max_gigabytes) p.max_gigabytes = Number(filters.max_gigabytes);
    if (filters.min_duration) p.min_duration = Number(filters.min_duration);
    if (filters.max_duration) p.max_duration = Number(filters.max_duration);
    if (filters.top_up_available !== "") p.top_up_available = filters.top_up_available === "true";
    if (filters.country_id) p.country_id = filters.country_id;
    if (filters.region_id) p.region_id = filters.region_id;
    if (showMine) p.has_carrier_package_id = true;

    return p;
  }, [page, pageSize, sortBy, sortOrder, filters, currency, showMine]);

  const resetFilters = () => {
    const empty = {
      id: "",
      name: "",
      description: "",
      currency: "",
      min_price: "",
      max_price: "",
      min_gigabytes: "",
      max_gigabytes: "",
      min_duration: "",
      max_duration: "",
      top_up_available: "",
      country_id: "",
      region_id: "",
    };
    setFilters(empty);
    setPage(1);
  };

  const isAllOnPageSelected = items.length > 0 && items.every((it) => selectedIds.has(it.id));
  const isAnyOnPageSelected = items.some((it) => selectedIds.has(it.id)) && !isAllOnPageSelected;

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isAllOnPageSelected) {
        items.forEach((it) => next.delete(it.id));
      } else {
        items.forEach((it) => next.add(it.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePackagesView = () => {
    setShowMine((v) => !v);
    setPage(1);
    setRefreshTick((x) => x + 1);
    scrollToTable();
  };

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, pageSize, sortBy, sortOrder, refreshTick, JSON.stringify(filters)]);

  useEffect(() => {
    setLoading(true);
    fetchCarrierPackages(params, token)
      .then((res) => {
        setItems(res?.items ?? []);
        setTotalRecords(res?.total ?? 0);
        setFilteredRecords(res?.filtered ?? 0);
      })
      .catch(() => {
        setItems([]);
        setTotalRecords(0);
        setFilteredRecords(0);
      })
      .finally(() => setLoading(false));
  }, [params, token, refreshTick]);

  useEffect(() => {
    setCarrierLoading(true);
    fetchCarrierAdmin()
      .then((data) => setCarrierInfo(data))
      .catch(() => setCarrierInfo(null))
      .finally(() => setCarrierLoading(false));
  }, [token]);

  const handleSort = (key) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortOrder("asc");
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortBy(null);
      setSortOrder(null);
    }
    setPage(1);
  };

  const handleMrgChange = async () => {
    const next = Number(tempMrg);
    if (Number.isNaN(next)) {
      alert("Enter valid MRG amount");
      setEditingMrg(false);
      return;
    }

    setSaving(true);
    const prev = carrierInfo;

    try {
      await updateCarrierAdmin({ markup_mrg: next });
      setCarrierInfo((prev) => (prev ? { ...prev, markup_mrg: next } : prev));
      setEditingMrg(false);
    } catch (e) {
      console.error(e);
      alert("Save failed!");
      setCarrierInfo(prev);
    } finally {
      setSaving(false);
      setEditingMrg(false);
    }
  };

  const handleMarkupChange = async () => {
    const nextPct = Number(tempMarkup);
    const next = nextPct / 100;
    setSaving(true);
    const prev = carrierInfo;

    try {
      await updateCarrierAdmin({ markup_rate: next });
      setCarrierInfo((prev) => (prev ? { ...prev, markup_rate: next } : prev));
      setEditingMarkup(false);
    } catch (e) {
      console.error(e);
      alert("Save failed");
    } finally {
      setSaving(false);
      setEditingMarkup(false);
      if (carrierInfo) {
        const pct = (carrierInfo.markup_rate * 100).toFixed(2);
        setMarkupInput(pct);
        setShowInputValue(true);
      }
    }
  };

  const handleApplySuggestedPrice = async (carrierPackageId, rowId) => {
    if (!carrierPackageId) return;

    setSuggestedSaving((s) => ({ ...s, [rowId]: true }));
    try {
      await setSuggestedPrice(carrierPackageId);
      setRefreshTick((x) => x + 1);
    } catch (err) {
      console.error(err);
      alert("Failed to apply suggested price");
    } finally {
      setSuggestedSaving((s) => ({ ...s, [rowId]: false }));
    }
  };

  const [draftDetailRate, setDraftDetailRate] = useState("");
  const [draftDetailMrg, setDraftDetailMrg] = useState("");

  const saveDetailRate = async () => {
    const valPct = Number(draftDetailRate);
    if (Number.isNaN(valPct) || valPct < 0) return;
    try {
      await batchUpdateSelected(
        {
          rows: items,
          selectedIds,
          draft: { markup_rate: valPct / 100 },
          mode: "rate",
        },
        token,
      );
      setDraftDetailMrg("");
      setDraftDetailRate("");
      setRefreshTick((x) => x + 1);
    } finally {
    }
  };

  const saveDetailMrg = async () => {
    const val = Number(draftDetailMrg);
    if (Number.isNaN(val)) return;
    try {
      await batchUpdateSelected({ rows: items, selectedIds, draft: { markup_mrg: val }, mode: "mrg" }, token);
      setDraftDetailRate("");
      setDraftDetailMrg("");
      setRefreshTick((x) => x + 1);
    } finally {
    }
  };

  const activateSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const draft = { active: true };
      await batchUpdateSelected({ rows: items, selectedIds, draft, mode: "activate" }, token);
      setRefreshTick((x) => x + 1);
    } finally {
    }
  };

  const inactivateSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const draft = { active: false };
      await batchUpdateSelected({ rows: items, selectedIds, draft, mode: "activate" }, token);
      setRefreshTick((x) => x + 1);
    } finally {
    }
  };

  const backToGlobal = async () => {
    if (selectedIds.size === 0) return;
    try {
      await batchUpdateSelected(
        {
          rows: items,
          selectedIds,
          draft: {},
          mode: "rateMrgNull",
        },
        token,
      );
      setDraftDetailRate("");
      setDraftDetailMrg("");
      setRefreshTick((x) => x + 1);
    } finally {
    }
  };

  const saveRowPrice = async (rowId, row, priceVal) => {
    setPriceSaving((s) => ({ ...s, [rowId]: true }));
    try {
      await upsertRowPrice(row, token, {
        price: Number(priceVal),
        automatic_price: false,
      });
      setPriceDirty((s) => ({ ...s, [rowId]: false }));
      setRefreshTick((x) => x + 1);
    } finally {
      setPriceSaving((s) => ({ ...s, [rowId]: false }));
    }
  };

  const setRowAutoPrice = async (rowId, row) => {
    setPriceSaving((s) => ({ ...s, [rowId]: true }));
    try {
      await upsertRowPrice(row, token, { automatic_price: true });
      setPriceDirty((s) => ({ ...s, [rowId]: false }));
      setRefreshTick((x) => x + 1);
    } finally {
      setPriceSaving((s) => ({ ...s, [rowId]: false }));
    }
  };

  const handleDownloadCsv = async () => {
    if (!token) return;
    setCsvDownloading(true);
    try {
      const blob = await downloadCarrierPackagesCsv(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `carrier_packages_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setCsvDownloading(false);
    }
  };

  const handleImportCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Please select a CSV file.");
      return;
    }

    setCsvImporting(true);
    try {
      await importCarrierPackagesCsv(file);
      setRefreshTick((tick) => tick + 1);
      alert("CSV imported successfully.");
    } catch (error) {
      alert(error.message || "CSV import failed.");
    } finally {
      setCsvImporting(false);
    }
  };

  const tableRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="partner-offers">
      <CsvImportInfoModal
        isOpen={csvInfoOpen}
        onClose={() => setCsvInfoOpen(false)}
        onContinue={() => {
          setCsvInfoOpen(false);
          fileInputRef.current?.click();
        }}
      />
      <PageHeader
        title="Partner Offers"
        description="Create and manage your partner offer based on our plans."
        actions={
          <div className="hero-actions">
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleImportCsv} hidden />

            <MainButton onClick={togglePackagesView} className="hero-btn">
              {!showMine ? "See My Packages" : "See GT Packages"}
            </MainButton>

            <SecondButton onClick={() => setCsvInfoOpen(true)} className="hero-btn" disabled={csvImporting}>
              <span className="hero-btn__arrow" aria-hidden="true">
                ↓
              </span>
              {csvImporting ? "Importing…" : "Import CSV"}
            </SecondButton>

            <SecondButton onClick={handleDownloadCsv} className="hero-btn btn-download" disabled={csvDownloading}>
              <span className="hero-btn__arrow" aria-hidden="true">
                ↑
              </span>
              <span className="btn-text">{csvDownloading ? "Preparing…" : "Export CSV"}</span>
            </SecondButton>
          </div>
        }
      />

      <section className="partner-offers__wrap">
        <>
          <div className="carrier-panel-wrap">
            <div className="pricing-panels">
              <div className="pricing-col">
                <div className="carrier-panel-head">
                  <h3 className="carrier-panel-title">Global pricing</h3>
                  <p className="carrier-panel-note">These settings apply to all partner packages for this account.</p>
                </div>

                <div className="carrier-panel compact">
                  <div className="carrier-card">
                    <div className="carrier-card__icon" aria-hidden="true">
                      %
                    </div>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      className="carrier-edit__input carrier-inline-input"
                      value={editingMarkup ? tempMarkup : (carrierInfo?.markup_rate ?? 0) * 100}
                      placeholder="Markup %"
                      onFocus={() => setEditingMarkup(true)}
                      onChange={(e) => setTempMarkup(e.target.value)}
                      disabled={saving}
                      aria-label="Markup percent"
                    />

                    <div className="carrier-card__actions">
                      <MainButton onClick={handleMarkupChange} disabled={saving || !editingMarkup} size="sm">
                        {saving ? "Saving…" : "Change"}
                      </MainButton>
                    </div>
                  </div>

                  <div className="carrier-card">
                    <div className="carrier-card__icon" aria-hidden="true">
                      €
                    </div>

                    <input
                      type="number"
                      step="0.01"
                      className="carrier-edit__input carrier-inline-input"
                      value={editingMrg ? tempMrg : (carrierInfo?.markup_mrg ?? 0)}
                      placeholder="MRG"
                      onFocus={() => setEditingMrg(true)}
                      onChange={(e) => setTempMrg(e.target.value)}
                      aria-label="MRG amount"
                      disabled={saving}
                    />

                    <div className="carrier-card__actions">
                      <MainButton onClick={handleMrgChange} disabled={saving || !editingMrg} size="sm">
                        {saving ? "Saving…" : "Change"}
                      </MainButton>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pricing-divider" aria-hidden="true"></div>

              <div className="pricing-col">
                <div className="carrier-panel-head detail-head">
                  <h3 className="carrier-panel-title">Detail pricing</h3>
                  <div className="detail-head-right">
                    <button
                      type="button"
                      className="detail-selected-badge"
                      disabled={selectedIds.size === 0 || saving}
                      onClick={backToGlobal}
                      title="Clear detailed Rate and MRG for selected"
                    >
                      Back to Global
                    </button>
                  </div>
                </div>

                <p className="carrier-panel-note">Applies to selected packages on this page.</p>

                <div className="carrier-panel compact">
                  <div className="carrier-card">
                    <div className="carrier-card__icon" aria-hidden="true">
                      %
                    </div>

                    <div className="carrier-card__content">
                      <div className="carrier-edit carrier-edit--inline">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="carrier-edit__input"
                          value={draftDetailRate}
                          onChange={(e) => setDraftDetailRate(e.target.value)}
                          aria-label="Detail markup percent"
                          disabled={saving}
                          placeholder="Markup %"
                        />
                      </div>
                    </div>

                    <div className="carrier-card__actions">
                      <MainButton
                        onClick={saveDetailRate}
                        disabled={saving || selectedIds.size === 0 || draftDetailRate === ""}
                      >
                        {saving ? "Saving…" : "Save"}
                      </MainButton>
                    </div>
                  </div>

                  <div className="carrier-card">
                    <div className="carrier-card__icon" aria-hidden="true">
                      €
                    </div>

                    <div className="carrier-card__content">
                      <div className="carrier-edit carrier-edit--inline">
                        <input
                          type="number"
                          step="0.01"
                          className="carrier-edit__input"
                          value={draftDetailMrg}
                          onChange={(e) => setDraftDetailMrg(e.target.value)}
                          disabled={saving}
                          placeholder="MRG"
                        />
                      </div>
                    </div>

                    <div className="carrier-card__actions">
                      <MainButton
                        onClick={saveDetailMrg}
                        disabled={saving || selectedIds.size === 0 || draftDetailMrg === ""}
                      >
                        {saving ? "Saving…" : "Save"}
                      </MainButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="po-toolbar " ref={tableRef}>
            <div className="po-sort">
              <span className="po-sort-label">Sort by:</span>
              <div className="select-wrapper">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="price">Price</option>
                  <option value="gigabytes">GB</option>
                  <option value="duration">Duration</option>
                  <option value="name">Name</option>
                </select>
              </div>
              <div className="select-wrapper" style={{ marginLeft: ".5rem" }}>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="asc">Asc</option>
                  <option value="desc">Desc</option>
                </select>
              </div>
            </div>

            <div className="po-stats">
              <span className="po-stat">Total: {totalRecords}</span>
              <span className="po-stat sep"> </span>
              <span className="po-stat">Filtered: {filteredRecords}</span>
              <span className="po-stat sep"> </span>
              <span className="po-stat">Selected: {selectedIds.size}</span>
            </div>
          </div>

          <div className="po-columns-row">
            <div className="po-columns">
              {optionalColumns.map((col) => (
                <button
                  key={col.key}
                  type="button"
                  className={`column-toggle-btn${visibleOptionals.includes(col.key) ? " active" : ""}`}
                  onClick={() => toggleColumn(col.key)}
                  aria-pressed={visibleOptionals.includes(col.key)}
                >
                  + {col.label}
                </button>
              ))}
            </div>
            <div className="po-pagesize">
              <span className="po-pagesize-label">Rows:</span>
              <div className="select-wrapper">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-actions">
                <button type="button" className="reset-filters-btn" onClick={resetFilters}>
                  Reset Filters
                </button>
              </div>
              <div className="filter-actions">
                <button
                  type="button"
                  className="btn-primary-a btn-activate"
                  disabled={selectedIds.size === 0 || saving}
                  onClick={activateSelected}
                >
                  Activate
                </button>
              </div>
              <div className="filter-actions">
                <button
                  type="button"
                  className="btn-danger btn-inactivate"
                  disabled={selectedIds.size === 0 || saving}
                  onClick={inactivateSelected}
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>

          <div className="po-filters-row">
            <div className="filters-grid">
              <div className="filter-field">
                <input
                  type="text"
                  value={filters.name}
                  onChange={(e) => {
                    setFilters({ ...filters, name: e.target.value });
                    setPage(1);
                  }}
                  placeholder="Search name"
                  aria-label="Search name"
                />
              </div>

              <div className="filter-field">
                <input
                  type="text"
                  value={filters.description}
                  onChange={(e) => {
                    setFilters({ ...filters, description: e.target.value });
                    setPage(1);
                  }}
                  placeholder="Search description"
                  aria-label="Search description"
                />
              </div>

              <div className="filter-field">
                <select
                  value={filters.currency}
                  onChange={(e) => {
                    setFilters({ ...filters, currency: e.target.value });
                    setPage(1);
                  }}
                  aria-label="Currency"
                >
                  <option value="">All currencies</option>
                  {ALL_CURRENCIES.map(({ id }) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <div className="filter-minmax">
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="Price min"
                    aria-label="Price min"
                    value={filters.min_price}
                    onChange={(e) => {
                      setFilters({ ...filters, min_price: e.target.value });
                      setPage(1);
                    }}
                  />
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="Price max"
                    aria-label="Price max"
                    value={filters.max_price}
                    onChange={(e) => {
                      setFilters({ ...filters, max_price: e.target.value });
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="filter-field">
                <div className="filter-minmax">
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="GB min"
                    aria-label="GB min"
                    value={filters.min_gigabytes}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        min_gigabytes: e.target.value,
                      });
                      setPage(1);
                    }}
                  />
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="GB max"
                    aria-label="GB max"
                    value={filters.max_gigabytes}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        max_gigabytes: e.target.value,
                      });
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="filter-field">
                <div className="filter-minmax">
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Days min"
                    aria-label="Duration min"
                    value={filters.min_duration}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        min_duration: e.target.value,
                      });
                      setPage(1);
                    }}
                  />
                  <input
                    type="number"
                    inputMode="numeric"
                    placeholder="Days max"
                    aria-label="Duration max"
                    value={filters.max_duration}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        max_duration: e.target.value,
                      });
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="filter-field">
                <input
                  type="text"
                  value={filters.country_id}
                  onChange={(e) => {
                    setFilters({ ...filters, country_id: e.target.value });
                    setPage(1);
                  }}
                  placeholder="Country (US/PL/ ..)"
                  aria-label="Country"
                />
              </div>

              <div className="filter-field">
                <input
                  type="text"
                  value={filters.region_id}
                  onChange={(e) => {
                    setFilters({ ...filters, region_id: e.target.value });
                    setPage(1);
                  }}
                  placeholder="Region (EU/ASP/ ..)"
                  aria-label="Region"
                />
              </div>
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="sel-col">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={isAllOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isAnyOnPageSelected;
                      }}
                      onChange={toggleSelectAllOnPage}
                    />
                  </th>
                  {visibleColumns.map((col) => {
                    const sortable = [
                      "id",
                      "automatic_price",
                      "description",
                      "name",
                      "region_id",
                      "active",
                      "price",
                      "suggested_price",
                      "gigabytes",
                      "duration",
                      "top_up_available",
                      "country_id",
                      "carrier_price",
                    ].includes(col.key);
                    if (!sortable) {
                      return (
                        <th key={col.key} className={`th-${col.key}`}>
                          <span className="th-inner">
                            <span className="th-label">{col.label}</span>
                          </span>
                        </th>
                      );
                    }

                    return (
                      <th key={col.key} onClick={() => handleSort(col.key)}>
                        <span className="th-inner">
                          <span className="th-label">{col.label}</span>
                          {sortBy === col.key && (
                            <img
                              src={sortOrder === "asc" ? sortUp : sortDown}
                              alt=""
                              className="sort-icon"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>Loading...</td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length}>No results</td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.id}>
                      <td className="sel-col">
                        <input
                          type="checkbox"
                          aria-label={`Select row ${it.id}`}
                          checked={selectedIds.has(it.id)}
                          onChange={() => toggleRow(it.id)}
                        />
                      </td>
                      {visibleColumns.map((col) => {
                        const k = col.key;
                        let val = it[k];

                        if (k === "carrier_price") {
                          const rowId = it.id;
                          const initial =
                            typeof priceDrafts[rowId] !== "undefined"
                              ? priceDrafts[rowId]
                              : Number.isFinite(Number(it.carrier_price))
                                ? Number(it.carrier_price).toFixed(2)
                                : "";

                          const value = String(initial);

                          const onChange = (e) => {
                            const v = e.target.value;
                            setPriceDrafts((s) => ({ ...s, [rowId]: v }));
                            const changed = v !== "" && Number(v) !== Number(it.carrier_price ?? NaN);
                            setPriceDirty((s) => ({
                              ...s,
                              [rowId]: changed,
                            }));
                          };

                          const onConfirm = async () => {
                            const num = Number(priceDrafts[rowId]);
                            if (Number.isNaN(num) || num < 0) return;
                            await saveRowPrice(rowId, it, num);
                          };

                          const onBackToAuto = async () => {
                            await setRowAutoPrice(rowId, it);
                          };

                          const showConfirm = priceDirty[rowId] === true;
                          const showBackToAuto = it.automatic_price === false;

                          return (
                            <td key={k} className="cell-price-edit">
                              <div className={`price-input-wrap ${priceSaving[rowId] ? "is-saving" : ""}`}>
                                <input
                                  type="number"
                                  className="price-input"
                                  value={value}
                                  onChange={onChange}
                                  disabled={priceSaving[rowId]}
                                />
                                {showConfirm && (
                                  <button
                                    type="button"
                                    className="icon-btn icon-confirm"
                                    onClick={onConfirm}
                                    disabled={priceSaving[rowId]}
                                    aria-label="Confirm price"
                                    title="Confirm price"
                                  />
                                )}
                                {showBackToAuto && !showConfirm && (
                                  <button
                                    type="button"
                                    className="icon-btn icon-reset"
                                    onClick={onBackToAuto}
                                    disabled={priceSaving[rowId]}
                                    aria-label="Back to automatic price"
                                    title="Back to automatic price"
                                  />
                                )}
                              </div>
                            </td>
                          );
                          // } else if (k === "suggested_price") {
                          //   const num = Number(val);
                          //   const formattedVal = Number.isFinite(num) ? num.toFixed(2) : "-";
                          //   const rowId = it.id;
                          //   const carrierPackageId = it.carrier_package_id;
                          //   const isSaving = suggestedSaving[rowId];

                          //   const canApply =
                          //     Boolean(carrierPackageId) &&
                          //     Number.isFinite(num) &&
                          //     num > 0 &&
                          //     Number(it.carrier_price) !== num;

                          //   return (
                          //     <td key={k} className="cell-suggested-price">
                          //       <div className="suggested-price-wrap">
                          //         <span>{formattedVal}</span>
                          //         {canApply && (
                          //           <button
                          //             type="button"
                          //             className="btn-apply-suggested-icon"
                          //             onClick={() => handleApplySuggestedPrice(carrierPackageId, rowId)}
                          //             disabled={isSaving}
                          //             title="Use suggested price"
                          //             aria-label="Use suggested price"
                          //           >
                          //             {isSaving ? "…" : "↵"}
                          //           </button>
                          //         )}
                          //       </div>
                          //     </td>
                          //   );
                        } else if (k === "markup_mrg") {
                          const isEmpty = val === null || val === undefined;
                          val = isEmpty ? "Global" : Number(val).toFixed(2);
                        } else if (k === "markup_rate") {
                          const isEmpty = val === null || val === undefined;
                          if (isEmpty) {
                            val = "Global";
                          } else {
                            const pct = (Number(val) * 100).toFixed(2);
                            val = `${pct}%`;
                          }
                        } else if (k === "duration" || k === "gigabytes") {
                          val = val === -1 ? "∞" : val;
                        } else if (typeof val === "boolean") {
                          if (k === "active") {
                            val = it.active ? (
                              <span className="badge-active">Active</span>
                            ) : (
                              <span className="badge-inactive">Inactive</span>
                            );
                          } else {
                            val = val ? "Yes" : "No";
                          }
                        } else if (
                          (typeof val === "number" && ["price"].includes(k)) ||
                          ["carrier_price", "suggested_price"].includes(k)
                        ) {
                          const num = Number(val);
                          val = Number.isFinite(num) ? num.toFixed(2) : "-";
                        } else if (val === null || val === undefined || val === "") {
                          val = "-";
                        }

                        return <td key={k}>{val}</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-bar-wide">
            <button
              className="pagination-btn left"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="pagination-center">Page {page}</span>
            <button
              className="pagination-btn right"
              onClick={() => setPage((p) => p + 1)}
              disabled={items.length < pageSize}
            >
              Next
            </button>
          </div>
        </>
      </section>
    </main>
  );
}
